import {
  alpha,
  shouldSkipGeneratingVar as muiShouldSkipGeneratingVar,
  type ThemeOptions,
} from '@mui/material/styles';
import { createFilterOptions } from '@mui/material/Autocomplete';
import type { Density } from './densityContext';
import type { ColorRamp, ThemeTokens } from './tokens';

declare module '@mui/material/styles' {
  interface Theme {
    /**
     * The resolved token set behind this theme. Prefer normal MUI paths
     * (`palette.primary.main`, `palette.divider`); reach for this only for
     * things MUI has no concept of — sidebar chrome, table rows, nav bar.
     */
    tokens: ThemeTokens;
    /** The information density this theme was built for. */
    density: Density;
  }
  interface ThemeOptions {
    tokens?: ThemeTokens;
    density?: Density;
  }
  interface PaletteColor {
    lighter?: string;
    darker?: string;
  }
  interface SimplePaletteColorOptions {
    lighter?: string;
    darker?: string;
  }
}

const brandColor = (r: ColorRamp) => ({
  main: r.main,
  light: r.light,
  dark: r.dark,
  lighter: r.lighter,
  darker: r.darker,
  contrastText: r.contrastText,
});

const statusColor = (r: ColorRamp) => ({
  main: r.main,
  light: r.light,
  dark: r.dark,
  contrastText: r.contrastText,
});

// Cell and card padding per density. Comfortable keeps MUI's defaults.
const TABLE_CELL_PADDING: Record<Density, { medium?: string; small?: string }> = {
  comfortable: {},
  compact: { medium: '10px 12px', small: '4px 12px' },
  dense: { medium: '6px 10px', small: '2px 8px' },
};
const CARD_PADDING: Record<Density, number | undefined> = {
  comfortable: undefined,
  compact: 12,
  dense: 8,
};

/**
 * A soft-UI ("neumorphic") shadow pair — a dark shadow on one side, a faint
 * highlight on the other, both on the same hue as the surface they sit on.
 * Skipped entirely on flat presets (`t.elevation.e1 === 'none'`), which
 * deliberately opt out of shadows in favour of hairline borders.
 */
function neumorphicShadow(mode: 'light' | 'dark', variant: 'raised' | 'pressed' | 'soft'): string {
  const darkAlpha = mode === 'dark' ? 0.55 : 0.12;
  const lightAlpha = mode === 'dark' ? 0.04 : 0.7;
  if (variant === 'pressed') {
    return `inset 3px 3px 6px ${alpha('#000000', darkAlpha)}, inset -2px -2px 5px ${alpha('#FFFFFF', lightAlpha)}`;
  }
  if (variant === 'soft') {
    return `2px 2px 5px ${alpha('#000000', darkAlpha)}, -1px -1px 3px ${alpha('#FFFFFF', lightAlpha)}`;
  }
  return `6px 6px 14px ${alpha('#000000', darkAlpha)}, -4px -4px 10px ${alpha('#FFFFFF', lightAlpha)}`;
}

function buildComponents(
  t: ThemeTokens,
  density: Density,
  mode: 'light' | 'dark',
): ThemeOptions['components'] {
  const controlRadius = t.component.button.radius;
  const cellPadding = TABLE_CELL_PADDING[density];
  const cardPadding = CARD_PADDING[density];
  const tight = density !== 'comfortable';
  // Flat presets (elevationStrength 0) opt out of shadows entirely — respect that instead of forcing depth on them.
  const flat = t.elevation.e1 === 'none';

  return {
    MuiCssBaseline: {
      styleOverrides: {
        html: { WebkitFontSmoothing: 'antialiased', height: '100%' },
        body: { height: '100%', overflowX: 'hidden' },
        '#root': { minHeight: '100%' },
        // Scrollbars are chrome: fixed greys read as a light streak on dark presets.
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { background: t.surface.sunken },
        '*::-webkit-scrollbar-thumb': {
          background: t.border.strong,
          borderRadius: 8,
          border: `2px solid ${t.surface.sunken}`,
        },
        '*::-webkit-scrollbar-thumb:hover': { background: t.text.disabled },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: controlRadius, fontWeight: t.typography.weightMedium },
        contained: flat
          ? {}
          : {
              boxShadow: neumorphicShadow(mode, 'raised'),
              transition: 'box-shadow 120ms ease',
              '&:hover': { boxShadow: neumorphicShadow(mode, 'raised') },
              '&:active': { boxShadow: neumorphicShadow(mode, 'pressed') },
            },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        filterOptions: createFilterOptions({ trim: true }),
        noOptionsText: 'No matches',
        loadingText: 'Loading...',
      },
      styleOverrides: {
        listbox: {
          maxHeight: 'min(240px, 40vh)',
          overflowY: 'auto',
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small', fullWidth: true, variant: 'outlined' },
    },
    MuiSelect: { defaultProps: { size: 'small' } },
    // A select's option list (and any menu) scrolls past ~8 rows instead of
    // covering the page; a menu's own sx maxHeight still wins.
    MuiMenu: {
      styleOverrides: {
        paper: { maxHeight: 'min(320px, calc(100% - 32px))' },
      },
    },
    MuiFormControl: { defaultProps: { size: 'small', fullWidth: true } },
    // The required-field asterisk is red on every label (InputLabel renders it through
    // FormLabel, so this covers text fields, selects, pickers and radio groups). A
    // disabled field gets no colour here, so its asterisk greys out with the label.
    MuiFormLabel: {
      styleOverrides: {
        asterisk: ({ ownerState }) => (ownerState.disabled ? {} : { color: t.status.error.main }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: controlRadius,
          backgroundColor: t.component.input.background,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: t.component.input.border },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: t.component.input.hoverBorder,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: t.component.input.focusBorder,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: t.component.input.errorBorder,
          },
          '&.Mui-disabled': { backgroundColor: t.component.input.disabledBackground },
        },
        input: {
          color: t.component.input.text,
          '&::placeholder': { color: t.component.input.placeholder, opacity: 1 },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${t.border.subtle}`,
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: flat
          ? {
              border: `1px solid ${t.border.subtle}`,
              borderRadius: t.radius.md,
            }
          : {
              border: 'none',
              borderRadius: t.radius.md,
              boxShadow: neumorphicShadow(mode, 'raised'),
              transition: 'box-shadow 150ms ease',
            },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': {
            backgroundColor:
              theme.palette.mode === 'light'
                ? alpha(theme.palette.primary.main, 0.04)
                : alpha(theme.palette.primary.main, 0.08),
          },
        }),
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: {
          backgroundColor: t.component.nav.background,
          color: t.component.nav.text,
          borderBottom: `1px solid ${t.component.nav.border}`,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: { minHeight: '56px !important' },
        dense: { minHeight: '48px !important' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: t.component.drawer.background },
      },
    },
    MuiDialog: {
      defaultProps: { maxWidth: 'sm', fullWidth: true },
      styleOverrides: { paper: { borderRadius: t.radius.lg } },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontSize: '0.9375rem', fontWeight: t.typography.weightBold } },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: t.typography.weightMedium, borderRadius: t.radius.sm },
        outlined: { borderColor: t.border.default },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: t.border.subtle } },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
    },
    MuiListItemButton: {
      styleOverrides: { root: { borderRadius: controlRadius } },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: t.typography.weightBold,
          fontSize: '0.75rem',
          backgroundColor: t.brand.primary.main,
          color: t.brand.primary.contrastText,
          boxShadow: flat ? 'none' : neumorphicShadow(mode, 'soft'),
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: t.component.table.rowHover },
          '&.Mui-selected': {
            backgroundColor: t.component.table.rowSelected,
            '&:hover': { backgroundColor: t.state.selectedHover },
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root:
          cardPadding === undefined
            ? {}
            : { padding: cardPadding, '&:last-child': { paddingBottom: cardPadding } },
      },
    },
    MuiMenuItem: { defaultProps: { dense: tight } },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: t.component.table.border,
          ...(cellPadding.medium && { padding: cellPadding.medium }),
        },
        sizeSmall: cellPadding.small ? { padding: cellPadding.small } : {},
        head: {
          fontWeight: t.typography.weightBold,
          color: t.component.table.headerText,
          backgroundColor: t.component.table.headerBackground,
        },
      },
    },
  };
}

// The token set is exposed on `theme.tokens` for components; `--cf-*` variables
// cover CSS, so MUI doesn't need to generate its own vars for it.
const shouldSkipGeneratingVar = (keys: string[]) =>
  keys[0] === 'tokens' || keys[0] === 'density' || muiShouldSkipGeneratingVar(keys);

/** Turns one mode's token set into MUI theme options. */
export function buildMuiTheme(
  t: ThemeTokens,
  mode: 'light' | 'dark',
  density: Density = 'comfortable',
): ThemeOptions {
  const headingSpacing = t.typography.headingLetterSpacing;
  // Scales a px size by how far this preset's base size sits from the 14px default,
  // so an accessibility/large-text preset still lands proportionally larger than this scale.
  const typeScale = t.typography.baseSize / 14;
  const remScale = (px: number) => `${(px * typeScale) / 16}rem`;

  return {
    tokens: t,
    density,
    cssVariables: {
      colorSchemeSelector: 'data-mui-color-scheme',
      shouldSkipGeneratingVar,
    },
    shape: { borderRadius: t.radius.md },
    palette: {
      mode,
      primary: brandColor(t.brand.primary),
      secondary: brandColor(t.brand.secondary),
      info: statusColor(t.status.info),
      success: statusColor(t.status.success),
      warning: statusColor(t.status.warning),
      error: statusColor(t.status.error),
      background: {
        default: t.surface.canvas,
        paper: t.surface.paper,
      },
      text: {
        primary: t.text.primary,
        secondary: t.text.secondary,
        disabled: t.text.disabled,
      },
      divider: t.border.subtle,
      // Feeds MUI's own hover/selected handling so unstyled components follow the preset.
      action: {
        hover: t.state.hover,
        selected: t.state.selected,
        disabled: t.text.disabled,
        disabledBackground: t.state.disabledBg,
        focus: t.state.pressed,
      },
      grey: {
        50: t.neutral.n50,
        100: t.neutral.n100,
        200: t.neutral.n200,
        300: t.neutral.n300,
        400: t.neutral.n400,
        500: t.neutral.n500,
        600: t.neutral.n600,
        700: t.neutral.n700,
        800: t.neutral.n800,
        900: t.neutral.n900,
      },
    },
    typography: {
      fontFamily: t.typography.fontFamily,
      fontSize: t.typography.baseSize,
      fontWeightRegular: t.typography.weightRegular,
      fontWeightMedium: t.typography.weightMedium,
      fontWeightBold: t.typography.weightBold,
      // A denser, LMS-standard scale — sized in px-over-16 rem and scaled by the
      // preset's own base size, so a preset that intentionally sets a larger
      // baseSize (e.g. an accessibility/large-text preset) still scales up from here.
      h1: {
        fontSize: remScale(24),
        fontWeight: t.typography.weightBold,
        lineHeight: 1.2,
        letterSpacing: headingSpacing,
      },
      h2: {
        fontSize: remScale(20),
        fontWeight: t.typography.weightBold,
        lineHeight: 1.25,
        letterSpacing: headingSpacing,
      },
      h3: {
        fontSize: remScale(17),
        fontWeight: t.typography.weightBold,
        lineHeight: 1.3,
        letterSpacing: headingSpacing,
      },
      h4: {
        fontSize: remScale(15),
        fontWeight: t.typography.weightMedium,
        lineHeight: 1.35,
        letterSpacing: headingSpacing,
      },
      h5: {
        fontSize: remScale(14),
        fontWeight: t.typography.weightMedium,
        letterSpacing: headingSpacing,
      },
      h6: {
        fontSize: remScale(13),
        fontWeight: t.typography.weightMedium,
        letterSpacing: headingSpacing,
      },
      subtitle1: { fontWeight: 500, fontSize: remScale(13), lineHeight: 1.4 },
      subtitle2: { fontWeight: t.typography.weightMedium, fontSize: remScale(12), lineHeight: 1.4 },
      body1: { fontSize: remScale(13), lineHeight: t.typography.lineHeight },
      body2: { fontSize: remScale(12), lineHeight: t.typography.lineHeight },
      caption: { fontSize: remScale(10.5), lineHeight: 1.5 },
      overline: { fontSize: remScale(10.5), lineHeight: 1.5 },
      button: { textTransform: 'none', fontWeight: t.typography.weightMedium, fontSize: remScale(12) },
    },
    // MUI expects exactly 25 entries; the token set has five meaningful steps.
    shadows: [
      t.elevation.e0,
      t.elevation.e1,
      t.elevation.e1,
      t.elevation.e2,
      t.elevation.e2,
      ...Array<string>(3).fill(t.elevation.e3),
      ...Array<string>(17).fill(t.elevation.e4),
    ] as ThemeOptions['shadows'],
    components: buildComponents(t, density, mode),
  };
}
